import React from 'react';
import {useIntl} from 'react-intl';
import {BudgetHeaderCard, FinancialNumber} from './ProgramBudgetTotals';
import {DeprecatedText as Text} from '@forecast-it/design-system';
import {NUMBER_TYPE} from '@/components/new-ui/project/project-budget-v3/util/BudgetUtils';
import {PROGRAM_BUDGET_TYPE} from '@/constants';
import {ProgramHeaderTooltips as HeaderTooltips} from './ProgramHeaderTooltips';

const ProfitSection = ({program}) => {
	const {formatMessage} = useIntl();

	return (
		<BudgetHeaderCard>
			<Text type={'heading'} variant={'m'} color={'primary'}>
				{formatMessage({id: 'common.profit_margin'})}
			</Text>
			<FinancialNumber
				translationId={'project_budget.actual_margin'}
				cy={'profitMargin.toDate'}
				value={program.projectsFinancialsToDate.recognitionActualProfitPercentage}
				numberType={NUMBER_TYPE.PERCENTAGE}
				size={'s'}
				tooltipComponent={HeaderTooltips.ActualMarginTooltip}
			/>
			<FinancialNumber
				translationId={'common.projected_total_margin'}
				cy={'profitMargin.atCompletion'}
				value={program.projectsFinancialsTotals.recognitionProfitPercentage}
				numberType={NUMBER_TYPE.PERCENTAGE}
				size={'s'}
				tooltipComponent={HeaderTooltips.ProjectedTotalMarginTooltip}
			/>
		</BudgetHeaderCard>
	);
};

const ActualRevenueSection = ({program, currencySymbol}) => {
	const {formatMessage} = useIntl();

	return (
		<BudgetHeaderCard>
			<Text type={'heading'} variant={'m'} color={'primary'}>
				{formatMessage({
					id: 'project_budget.actual_revenue',
				})}
			</Text>
			<FinancialNumber
				translationId={'project_budget.actual_revenue'}
				cy={'revenueRecognition.toDate'}
				value={program.projectsFinancialsToDate.totalActualRevenueRecognition}
				numberType={NUMBER_TYPE.MONEY}
				currencySymbol={currencySymbol}
				size={'s'}
				tooltipComponent={HeaderTooltips.ActualRevenueTooltip}
			/>
			<FinancialNumber
				translationId={'program_budget.variance_with_cap'}
				cy={'revenueRecognition.toDate.variance'}
				value={program.budgetValue - program.projectsFinancialsToDate.totalActualRevenueRecognition}
				numberType={NUMBER_TYPE.MONEY}
				currencySymbol={currencySymbol}
				size={'s'}
				tooltipComponent={HeaderTooltips.VarianceWithCapActualRevenueTooltip}
			/>
		</BudgetHeaderCard>
	);
};

const ProjectedTotalRevenueSection = ({program, currencySymbol}) => {
	const {formatMessage} = useIntl();

	return (
		<BudgetHeaderCard>
			<Text type={'heading'} variant={'m'} color={'primary'}>
				{formatMessage({
					id: 'common.projected_total_revenue',
				})}
			</Text>
			<FinancialNumber
				translationId={'common.projected_total_revenue'}
				cy={'revenueRecognition.atCompletion'}
				value={program.projectsFinancialsTotals.recognitionForecastRevenue}
				numberType={NUMBER_TYPE.MONEY}
				currencySymbol={currencySymbol}
				size={'s'}
				tooltipComponent={HeaderTooltips.ProjectedTotalRevenueTooltip}
			/>
			<FinancialNumber
				translationId={'program_budget.variance_with_cap'}
				cy={'revenueRecognition.atCompletion.variance'}
				value={program.budgetValue - program.projectsFinancialsTotals.recognitionForecastRevenue}
				numberType={NUMBER_TYPE.MONEY}
				currencySymbol={currencySymbol}
				size={'s'}
				tooltipComponent={HeaderTooltips.VarianceWithCapProjectedTotalRevenueTooltip}
			/>
		</BudgetHeaderCard>
	);
};
const RevenueSection = ({program, currencySymbol}) => {
	const {formatMessage} = useIntl();
	const isFixedPrice = program.budgetType === PROGRAM_BUDGET_TYPE.FIXED_PRICE;

	return (
		<BudgetHeaderCard>
			<Text type={'heading'} variant={'m'} color={'primary'}>
				{formatMessage({id: 'project_budget.revenue'})}
			</Text>
			<FinancialNumber
				translationId={'project_budget.actual_revenue'}
				cy={'revenueRecognition.toDate'}
				value={program.projectsFinancialsToDate.totalActualRevenueRecognition}
				numberType={NUMBER_TYPE.MONEY}
				currencySymbol={currencySymbol}
				size={'s'}
				tooltipComponent={HeaderTooltips.ActualRevenueTooltip}
			/>
			{isFixedPrice ? (
				<FinancialNumber
					translationId={'common.remaining_budget'}
					cy={'revenueRecognition.remaining'}
					value={program.budgetValue - program.projectsFinancialsToDate.totalActualRevenueRecognition}
					numberType={NUMBER_TYPE.MONEY}
					currencySymbol={currencySymbol}
					size={'s'}
					tooltipComponent={HeaderTooltips.RemainingBudgetTooltip}
				/>
			) : (
				<FinancialNumber
					translationId={'common.projected_total_revenue'}
					cy={'revenueRecognition.atCompletion'}
					value={program.projectsFinancialsTotals.recognitionForecastRevenue}
					numberType={NUMBER_TYPE.MONEY}
					currencySymbol={currencySymbol}
					size={'s'}
					tooltipComponent={HeaderTooltips.ProjectedTotalRevenueTooltip}
				/>
			)}
		</BudgetHeaderCard>
	);
};

const ActualValueOfServiceSection = ({program, currencySymbol}) => {
	const {formatMessage} = useIntl();

	return (
		<BudgetHeaderCard>
			<Text type={'heading'} variant={'m'} color={'primary'}>
				{formatMessage({
					id: 'common.actual_value_of_service',
				})}
			</Text>
			<FinancialNumber
				translationId={'common.actual_value_of_service'}
				cy={'valueOfService.toDate'}
				value={program.projectsFinancialsTotals.allActualTimeAndExpenses}
				numberType={NUMBER_TYPE.MONEY}
				currencySymbol={currencySymbol}
				size={'s'}
				tooltipComponent={HeaderTooltips.ActualValueOfServiceTooltip}
			/>
			{program.budgetType === PROGRAM_BUDGET_TYPE.FIXED_PRICE ? (
				<FinancialNumber
					translationId={'program_budget.variance_with_fixed_price'}
					cy={'valueOfService.toDate.variance'}
					value={program.budgetValue - program.projectsFinancialsTotals.allActualTimeAndExpenses}
					numberType={NUMBER_TYPE.MONEY}
					currencySymbol={currencySymbol}
					size={'s'}
					tooltipComponent={HeaderTooltips.VarianceWithFixedPriceActualValueOfServiceTooltip}
				/>
			) : (
				<FinancialNumber
					translationId={'program_budget.variance_with_cap'}
					cy={'valueOfService.toDate.variance'}
					value={program.budgetValue - program.projectsFinancialsTotals.allActualTimeAndExpenses}
					numberType={NUMBER_TYPE.MONEY}
					currencySymbol={currencySymbol}
					size={'s'}
					tooltipComponent={HeaderTooltips.VarianceWithCapActualValueOfServiceTooltip}
				/>
			)}
		</BudgetHeaderCard>
	);
};

const ProjectedTotalValueOfServiceSection = ({program, currencySymbol}) => {
	const {formatMessage} = useIntl();

	return (
		<BudgetHeaderCard>
			<Text type={'heading'} variant={'m'} color={'primary'}>
				{formatMessage({
					id: 'common.projected_total_value_of_service',
				})}
			</Text>
			<FinancialNumber
				translationId={'common.projected_total_value_of_service'}
				cy={'valueOfService.atCompletion'}
				value={program.projectsFinancialsTotals.allTotalTimeAndExpensesAtCompletion}
				numberType={NUMBER_TYPE.MONEY}
				currencySymbol={currencySymbol}
				size={'s'}
				tooltipComponent={HeaderTooltips.ProjectedTotalValueOfServiceTooltip}
			/>
			{program.budgetType === PROGRAM_BUDGET_TYPE.FIXED_PRICE ? (
				<FinancialNumber
					translationId={'program_budget.variance_with_fixed_price'}
					cy={'valueOfService.atCompletion.variance'}
					value={program.budgetValue - program.projectsFinancialsTotals.allTotalTimeAndExpensesAtCompletion}
					numberType={NUMBER_TYPE.MONEY}
					currencySymbol={currencySymbol}
					size={'s'}
					tooltipComponent={HeaderTooltips.VarianceWithFixedPriceProjectedTotalValueOfServiceTooltip}
				/>
			) : (
				<FinancialNumber
					translationId={'program_budget.variance_with_cap'}
					cy={'valueOfService.atCompletion.variance'}
					value={program.budgetValue - program.projectsFinancialsTotals.allTotalTimeAndExpensesAtCompletion}
					numberType={NUMBER_TYPE.MONEY}
					currencySymbol={currencySymbol}
					size={'s'}
					tooltipComponent={HeaderTooltips.VarianceWithCapProjectedTotalValueOfServiceTooltip}
				/>
			)}
		</BudgetHeaderCard>
	);
};

const ValueOfServiceSection = ({program, currencySymbol}) => {
	const {formatMessage} = useIntl();

	return (
		<BudgetHeaderCard>
			<Text type={'heading'} variant={'m'} color={'primary'}>
				{formatMessage({id: 'common.value_of_service'})}
			</Text>
			<FinancialNumber
				translationId={'common.actual_value_of_service'}
				cy={'valueOfService.toDate'}
				value={program.projectsFinancialsTotals.allActualTimeAndExpenses}
				numberType={NUMBER_TYPE.MONEY}
				currencySymbol={currencySymbol}
				size={'s'}
				tooltipComponent={HeaderTooltips.ActualValueOfServiceTooltip}
			/>
			<FinancialNumber
				translationId={'common.projected_total_value_of_service'}
				cy={'valueOfService.atCompletion'}
				value={program.projectsFinancialsTotals.allTotalTimeAndExpensesAtCompletion}
				numberType={NUMBER_TYPE.MONEY}
				currencySymbol={currencySymbol}
				size={'s'}
				tooltipComponent={HeaderTooltips.ProjectedTotalValueOfServiceTooltip}
			/>
		</BudgetHeaderCard>
	);
};

export const ProgramHeaderSections = {
	ProfitSection,
	ActualRevenueSection,
	ProjectedTotalRevenueSection,
	RevenueSection,
	ActualValueOfServiceSection,
	ProjectedTotalValueOfServiceSection,
	ValueOfServiceSection,
};
